import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../../../lib/api';
const API = API_BASE;
import ItemMasterDynamic from './ItemMasterDynamic';
import '../../../styles/item-list.css';

function hdrs() {
  const t = localStorage.getItem('retailcliq_token') || sessionStorage.getItem('retailcliq_token') || '';
  return { 'Content-Type': 'application/json', Authorization: t ? `Bearer ${t}` : '' };
}

const STATUS_OPTIONS = [
  { value: 'active',   label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: '',         label: 'All Status' },
];

export default function ItemListScreen({ theme, onClose }) {
  const [mode, setMode] = useState('list'); // 'list' | 'detail'
  const [editItemId, setEditItemId] = useState(null);

  // List state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [q, setQ] = useState('');
  const [division, setDivision] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [status, setStatus] = useState('active');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [showFilters, setShowFilters] = useState(false);

  // Lookup data for filter dropdowns
  const [divisions, setDivisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const searchRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const p = new URLSearchParams({ q, division, category, brand, status, page, limit });
      const r = await fetch(`${API}/api/inventory/items?${p}`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) {
        setItems(j.data || []);
        setTotal(j.total || 0);
      } else {
        setError(j.message || 'Load failed');
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [q, division, category, brand, status, page, limit]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Load lookup data for filter dropdowns from SearchInfo Mode B
  useEffect(() => {
    const loadSearchInfo = async (screenId, controlId, setter) => {
      try {
        const r = await fetch(`${API}/api/inventory/getinfo/screen/${encodeURIComponent(screenId)}/control/${encodeURIComponent(controlId)}?compId=C001`, { headers: hdrs() });
        const j = await r.json();
        if (j.success && j.data?.length) {
          setter(j.data.map(row => ({ label: row[j.displayMember], value: row[j.valueMember] })));
        }
      } catch {}
    };
    loadSearchInfo('5.5.8', 1, setDivisions);   // Division: DivName/DivUId
    loadSearchInfo('5.5.2', 1, setCategories);  // Category: CatName/CatId
    loadSearchInfo('5.5.3', 1, setBrands);      // Brand: BrandName/BrandId
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchItems();
  };

  const handleClearFilters = () => {
    setQ(''); setDivision(''); setCategory(''); setBrand(''); setStatus('active');
    setPage(1);
  };

  const openDetail = (itemId) => {
    setEditItemId(itemId);
    setMode('detail');
  };

  const openNew = () => {
    setEditItemId(null);
    setMode('detail');
  };

  const closeDetail = () => {
    setMode('list');
    setEditItemId(null);
    fetchItems(); // refresh list after edit
  };

  const totalPages = Math.ceil(total / limit) || 1;

  if (mode === 'detail') {
    return (
      <ItemMasterDynamic
        theme={theme}
        editItemId={editItemId}
        onClose={closeDetail}
      />
    );
  }

  return (
    <div className="il-screen" data-testid="item-list-screen">
      {/* ── Top bar ── */}
      <div className="il-topbar">
        <div className="il-topbar-left">
          {onClose && (
            <button className="il-back-btn" onClick={onClose} data-testid="item-list-back">
              ← Back
            </button>
          )}
          <div>
            <div className="il-breadcrumb">ERP / Item Master</div>
            <h1 className="il-title">Item Master List</h1>
          </div>
        </div>
        <div className="il-topbar-right">
          <span className="il-total-badge">{total} items</span>
          <button
            className="il-btn il-btn-outline"
            onClick={() => setShowFilters(f => !f)}
            data-testid="toggle-filters-btn"
          >
            {showFilters ? 'Hide Filters' : 'Filters'}
          </button>
          <button
            className="il-btn il-btn-primary"
            onClick={openNew}
            data-testid="new-item-btn"
          >
            + New Item
          </button>
        </div>
      </div>

      {/* ── Inline search bar ── */}
      <form className="il-search-bar" onSubmit={handleSearch}>
        <input
          ref={searchRef}
          className="il-search-inp"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search item code, name, barcode…"
          data-testid="item-search-input"
        />
        <button type="submit" className="il-btn il-btn-primary" data-testid="item-search-btn">
          Search
        </button>
      </form>

      <div className="il-body">
        {/* ── Filter panel ── */}
        {showFilters && (
          <div className="il-filter-panel" data-testid="filter-panel">
            <div className="il-filter-header">
              <span>Filters</span>
              <button className="il-clr-btn" onClick={handleClearFilters}>Clear All</button>
            </div>

            <div className="il-filter-group">
              <label className="il-filter-lbl">Division</label>
              <select
                className="il-filter-sel"
                value={division}
                onChange={e => { setDivision(e.target.value); setPage(1); }}
                data-testid="filter-division"
              >
                <option value="">All Divisions</option>
                {divisions.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>

            <div className="il-filter-group">
              <label className="il-filter-lbl">Category</label>
              <select
                className="il-filter-sel"
                value={category}
                onChange={e => { setCategory(e.target.value); setPage(1); }}
                data-testid="filter-category"
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            <div className="il-filter-group">
              <label className="il-filter-lbl">Brand</label>
              <select
                className="il-filter-sel"
                value={brand}
                onChange={e => { setBrand(e.target.value); setPage(1); }}
                data-testid="filter-brand"
              >
                <option value="">All Brands</option>
                {brands.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>

            <div className="il-filter-group">
              <label className="il-filter-lbl">Status</label>
              <div className="il-status-tabs">
                {STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className={`il-status-tab${status === opt.value ? ' active' : ''}`}
                    onClick={() => { setStatus(opt.value); setPage(1); }}
                    data-testid={`status-tab-${opt.value || 'all'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Grid ── */}
        <div className="il-grid-wrap">
          {error && <div className="il-error">{error}</div>}
          {loading ? (
            <div className="il-loading">Loading…</div>
          ) : items.length === 0 ? (
            <div className="il-empty">
              <p>No items found</p>
              <button className="il-btn il-btn-primary" onClick={openNew}>Create First Item</button>
            </div>
          ) : (
            <>
              <table className="il-table" data-testid="item-list-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Article Name</th>
                    <th>Item Name / SKU</th>
                    <th>Division</th>
                    <th>Category</th>
                    <th>MRP</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr
                      key={item.itemId}
                      className="il-row"
                      onClick={() => openDetail(item.itemId)}
                      data-testid={`item-row-${item.itemId}`}
                    >
                      <td className="il-td il-td-code">{item.articleCode || item.itemId}</td>
                      <td className="il-td il-td-name">{item.articleName || '—'}</td>
                      <td className="il-td">{item.itemName || item.skuPlu || '—'}</td>
                      <td className="il-td">{item.divisionId || '—'}</td>
                      <td className="il-td">{item.categoryId || '—'}</td>
                      <td className="il-td il-td-mrp">{item.mrp ? `${item.mrp}` : '—'}</td>
                      <td className="il-td">
                        <span className={`il-status-badge ${item.isActive ? 'active' : 'inactive'}`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="il-td il-td-date">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="il-td il-td-actions">
                        <button
                          className="il-edit-btn"
                          onClick={e => { e.stopPropagation(); openDetail(item.itemId); }}
                          data-testid={`edit-item-${item.itemId}`}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="il-pagination" data-testid="pagination">
                  <button
                    className="il-page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    ← Prev
                  </button>
                  <span className="il-page-info">Page {page} of {totalPages} ({total} total)</span>
                  <button
                    className="il-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
